import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getDiagnosisReportById, deleteReport } from '../../services/diagnosisReports.service';
import { calculateFinancialDiagnosis, calculateBusinessDiagnosis } from '../../utils/diagnosisEngine';
import { calculateBusinessTax } from '../../utils/taxCalculations';
import { generateProfessionalPDF } from '../../utils/pdfGenerator';
import '../../styles/admin-diagnosis.css';

const DiagnosisReportDetail = () => {
    const { reportId } = useParams();
    const navigate = useNavigate();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [calculations, setCalculations] = useState(null);

    useEffect(() => {
        fetchReport();
    }, [reportId]);

    const fetchReport = async () => {
        try {
            setLoading(true);
            const data = await getDiagnosisReportById(reportId);
            setReport(data);
            if (data) {
                performCalculations(data);
            }
        } catch (error) {
            console.error('Error fetching report:', error);
            alert('Error loading report. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const performCalculations = (data) => {
        const incomeType = data.personalInfo?.incomeType || 'salary';

        // SALARY INCOME DIAGNOSIS CALCULATION (NEW)
        if (incomeType === 'salary') {
            const diagnosisResults = calculateFinancialDiagnosis(data);

            const baseSalary = parseFloat(data.personalInfo?.monthlySalary) || 0;
            const otherIncome = parseFloat(data.personalInfo?.otherIncomeAmount) || 0;
            const monthlySalary = baseSalary + otherIncome;
            const annualSal = monthlySalary * 12;

            const standardDeductionOld = 50000;
            const standardDeductionNew = 75000;

            const epfAnnual = (parseFloat(data.salaryIncome?.epf) || 0) * 12;
            const ppfAnnual = (parseFloat(data.salaryIncome?.ppf) || 0) * 12;
            const elssAnnual = (parseFloat(data.salaryInvestment?.elss) || 0) * 12;
            const homeLoanPrincipal = (parseFloat(data.salaryIncome?.homeLoanEMI) || 0) * 12 * 0.40; // Assume 40% principal roughly
            const insurance80C = (parseFloat(data.salaryIncome?.lifeInsurance) || 0) * 12;

            const raw80C = epfAnnual + ppfAnnual + elssAnnual + homeLoanPrincipal + insurance80C;
            const total80C = Math.min(150000, raw80C);

            const hraExemption = parseFloat(data.taxDetails?.hraExemption) || 0;
            const homeLoanInterest = Math.min(200000, (parseFloat(data.salaryIncome?.homeLoanEMI) || 0) * 12 * 0.60);
            const insurance80D = (parseFloat(data.salaryIncome?.healthInsurance) || 0) * 12;

            const totalDeductionsOld = standardDeductionOld + total80C + insurance80D + hraExemption + homeLoanInterest;
            const taxableIncomeOld = Math.max(0, annualSal - totalDeductionsOld);
            const taxableIncomeNew = Math.max(0, annualSal - standardDeductionNew);

            const oldTaxResult = calculateTaxOldRegime(taxableIncomeOld);
            const newTaxResult = calculateTaxNewRegime(taxableIncomeNew);

            setCalculations({
                ...diagnosisResults,
                incomeType: 'salary',
                monthlySalary,
                annualSalary: annualSal,
                hraExemption,
                homeLoanInterest,
                bestRegime: (oldTaxResult.totalTax <= newTaxResult.totalTax) ? 'Old Regime' : 'New Regime',
                taxSavings: Math.abs(oldTaxResult.totalTax - newTaxResult.totalTax),
                oldRegimeTax: oldTaxResult.totalTax,
                newRegimeTax: newTaxResult.totalTax,
                healthScore: diagnosisResults.grades.overall,

                // Add fields for PDF compatibility
                standardDeductionOld,
                standardDeductionNew,
                total80C,
                insurance80D,
                totalDeductionsOld,
                taxableIncomeOld,
                taxableIncomeNew,
                oldRegimeTaxBeforeCess: oldTaxResult.taxBeforeCess,
                newRegimeTaxBeforeCess: newTaxResult.taxBeforeCess,
                monthlySavings: diagnosisResults.metrics.totalMonthlyInvestments,
                totalExpenses: diagnosisResults.metrics.totalEssentialExpenses + diagnosisResults.metrics.totalLifestyleExpenses,
                savingsPercentage: parseFloat(diagnosisResults.metrics.savingsRate.toFixed(1)),
                totalTaxPayable: (oldTaxResult.totalTax <= newTaxResult.totalTax) ? oldTaxResult.totalTax : newTaxResult.totalTax
            });
            return;
        }

        if (incomeType === 'business' || incomeType === 'business_partnership') {
            const bizDiagnosis = calculateBusinessDiagnosis(data);
            const businessDetails = data.businessDetails || {};
            const businessInvestment = data.businessInvestment || {};

            const salesTurnover = parseFloat(businessDetails.grossTurnover || 0);
            const otherIncome = (parseFloat(businessInvestment?.savingInterest || 0)) +
                (parseFloat(businessInvestment?.fdInterest || 0)) +
                (parseFloat(businessInvestment?.rdInterest || 0)) +
                (parseFloat(businessInvestment?.licMaturity || 0)) +
                (parseFloat(businessInvestment?.agriIncome || 0));

            const taxCalc = calculateBusinessTax({
                salesTurnover,
                otherIncome,
                totalExpenses: bizDiagnosis.metrics.totalCOGS + bizDiagnosis.metrics.totalOpEx
            });

            setCalculations({
                ...bizDiagnosis,
                incomeType: 'business',
                businessType: businessDetails.businessType,
                salesTurnover: taxCalc.salesTurnover,
                otherIncome: taxCalc.otherIncome,
                totalIncome: taxCalc.totalIncome,
                totalExpenses: taxCalc.totalExpenses,
                businessProfit: taxCalc.businessProfit,
                totalTaxableIncome: taxCalc.totalTaxableIncome,
                netProfitBeforeTax: taxCalc.netProfitBeforeTax,
                totalTaxPayable: taxCalc.totalTaxPayable,
                monthlyIncome: bizDiagnosis.metrics.monthlyRevenue,
                monthlyExpenses: (bizDiagnosis.metrics.totalCOGS + bizDiagnosis.metrics.totalOpEx) / 12,
                monthlySavings: bizDiagnosis.metrics.monthlyProfit,
                savingsPercentage: bizDiagnosis.metrics.netMargin.toFixed(1),
                healthScore: bizDiagnosis.grades.overall,
                bestRegime: 'New Regime (Business)',
                taxSavings: 0,
                taxableIncomeNew: taxCalc.netProfitBeforeTax
            });
            return;
        }
    };

    const calculateTaxOldRegime = (taxableIncome) => {
        let tax = 0;

        if (taxableIncome <= 250000) return { totalTax: 0, taxBeforeCess: 0 };

        if (taxableIncome > 250000) {
            const slab2 = Math.min(taxableIncome - 250000, 250000);
            tax += slab2 * 0.05;
        }

        if (taxableIncome > 500000) {
            const slab3 = Math.min(taxableIncome - 500000, 500000);
            tax += slab3 * 0.20;
        }

        if (taxableIncome > 1000000) {
            const slab4 = taxableIncome - 1000000;
            tax += slab4 * 0.30;
        }

        let surcharge = 0;
        if (taxableIncome > 5000000 && taxableIncome <= 10000000) {
            surcharge = tax * 0.10;
        } else if (taxableIncome > 10000000) {
            surcharge = tax * 0.15;
        }

        const taxWithSurcharge = tax + surcharge;
        const cess = 0; // Removed per user request

        return { totalTax: taxWithSurcharge + cess, taxBeforeCess: taxWithSurcharge };
    };

    const calculateTaxNewRegime = (taxableIncome) => {
        let tax = 0;

        // FY 2025-26 Slabs (New Regime)
        if (taxableIncome <= 300000) return { totalTax: 0, taxBeforeCess: 0 };

        if (taxableIncome > 300000) {
            const slab2 = Math.min(taxableIncome - 300000, 400000); // 3-7L @ 5%
            tax += slab2 * 0.05;
        }

        if (taxableIncome > 700000) {
            const slab3 = Math.min(taxableIncome - 700000, 300000); // 7-10L @ 10%
            tax += slab3 * 0.10;
        }

        if (taxableIncome > 1000000) {
            const slab4 = Math.min(taxableIncome - 1000000, 200000); // 10-12L @ 15%
            tax += slab4 * 0.15;
        }

        if (taxableIncome > 1200000) {
            const slab5 = Math.min(taxableIncome - 1200000, 300000); // 12-15L @ 20%
            tax += slab5 * 0.20;
        }

        if (taxableIncome > 1500000) {
            const slab6 = taxableIncome - 1500000; // >15L @ 30%
            tax += slab6 * 0.30;
        }

        let surcharge = 0;
        if (taxableIncome > 5000000 && taxableIncome <= 10000000) {
            surcharge = tax * 0.10;
        } else if (taxableIncome > 10000000) {
            surcharge = tax * 0.15;
        }

        const taxWithSurcharge = tax + surcharge;

        // Rebate u/s 87A: Income <= 7L is tax free
        let finalTax = taxWithSurcharge;
        if (taxableIncome <= 700000) {
            finalTax = 0;
        }

        const cess = 0; // Removed per user request

        return { totalTax: finalTax + cess, taxBeforeCess: finalTax };
    };


    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(value || 0);
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getHealthScoreClass = (score) => {
        if (!score) return '';
        const scoreLower = String(score).toLowerCase(); // Ensure string
        if (score >= 80 || scoreLower === 'excellent') return 'health-excellent';
        if (score >= 60 || scoreLower === 'good') return 'health-good';
        if (score >= 40 || scoreLower === 'fair') return 'health-fair';
        return 'health-poor';
    };

    const generatePDF = (e) => {
        const btn = e.currentTarget;
        if (!report || !calculations) {
            console.warn("Cannot generate PDF: Missing report or calculations data.");
            return;
        }

        const originalText = btn.innerHTML;
        btn.innerHTML = "Generating...";
        btn.disabled = true;

        try {
            console.log("Starting professional PDF generation...");
            setTimeout(() => {
                try {
                    generateProfessionalPDF(report, calculations);
                } catch (err) {
                    console.error("PDF Generation Error:", err);
                    alert("Error generating PDF: " + err.message);
                } finally {
                    btn.innerHTML = originalText;
                    btn.disabled = false;
                }
            }, 100);
        } catch (err) {
            console.error("PDF Generation Trigger Error:", err);
            btn.innerHTML = originalText;
            btn.disabled = false;
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this report? This action cannot be undone.')) {
            try {
                await deleteReport(reportId);
                alert('Report deleted successfully');
                navigate('/admin/diagnosis/reports');
            } catch (error) {
                console.error('Error deleting report:', error);
                alert('Error deleting report. Please try again.');
            }
        }
    };

    if (loading) {
        return (
            <div className="admin-content-v2" style={{ display: 'flex', minHeight: '80vh' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', flex: 1 }}>
                    <div className="custom-loader" style={{ width: '55px', height: '80px' }}>
                        <svg viewBox="0 0 55 80" xmlns="http://www.w3.org/2000/svg" fill="#0EAB5EFF">
                            <g transform="matrix(1 0 0 -1 0 80)">
                                <rect width="10" height="20" rx="3">
                                    <animate attributeName="height" begin="0s" dur="4.3s" values="20;45;57;80;64;32;66;45;64;23;66;13;64;56;34;34;2;23;76;79;20" calcMode="linear" repeatCount="indefinite" />
                                </rect>
                                <rect x="15" width="10" height="80" rx="3">
                                    <animate attributeName="height" begin="0s" dur="2s" values="80;55;33;5;75;23;73;33;12;14;60;80" calcMode="linear" repeatCount="indefinite" />
                                </rect>
                                <rect x="30" width="10" height="50" rx="3">
                                    <animate attributeName="height" begin="0s" dur="1.4s" values="50;34;78;23;56;23;34;76;80;54;21;50" calcMode="linear" repeatCount="indefinite" />
                                </rect>
                                <rect x="45" width="10" height="30" rx="3">
                                    <animate attributeName="height" begin="0s" dur="2s" values="30;45;13;80;56;72;45;76;34;23;67;30" calcMode="linear" repeatCount="indefinite" />
                                </rect>
                            </g>
                        </svg>
                    </div>
                    <p style={{ marginTop: '20px', color: '#64748B', fontWeight: 500 }}>Loading report details...</p>
                </div>
            </div>
        );
    }

    if (!report) {
        return (
            <div className="admin-diagnosis-page">
                <div className="no-reports">
                    <p>Report not found.</p>
                    <Link to="/admin/diagnosis/reports" className="back-link">← Back to Reports</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-content-v2">
            {/* Header Area */}
            <div className="detail-header-v2">
                <div>
                    <Link to="/admin/diagnosis/reports" className="back-btn-v2">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
                        Back to Reports
                    </Link>
                    <h1 className="nav-title-v2">Patient Diagnosis Deep-Dive</h1>
                    <p className="nav-subtitle-v2" style={{ fontFamily: 'monospace' }}>Report ID: {report.reportId}</p>
                </div>
                <div className="header-actions-v2">
                    <button onClick={generatePDF} className="btn-v2 primary">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                        Export PDF
                    </button>
                    <button onClick={handleDelete} className="btn-v2 danger">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                        Delete
                    </button>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '24px' }}>
                <div className="main-details-v2">
                    {/* Personal Information */}
                    <div className="detail-card-v2">
                        <h2>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                            Personal Profile
                        </h2>
                        <div className="detail-grid-v2">
                            <div className="detail-item-v2">
                                <span className="detail-label-v2">Full Name</span>
                                <span className="detail-value-v2">{report.personalInfo?.name || 'N/A'}</span>
                            </div>
                            <div className="detail-item-v2">
                                <span className="detail-label-v2">Email Address</span>
                                <span className="detail-value-v2" style={{ fontSize: '13px' }}>{report.personalInfo?.email || 'N/A'}</span>
                            </div>
                            <div className="detail-item-v2">
                                <span className="detail-label-v2">Contact Number</span>
                                <span className="detail-value-v2">{report.personalInfo?.phone || 'N/A'}</span>
                            </div>
                            <div className="detail-item-v2">
                                <span className="detail-label-v2">Age</span>
                                <span className="detail-value-v2">{report.personalInfo?.age || 'N/A'}</span>
                            </div>
                            <div className="detail-item-v2">
                                <span className="detail-label-v2">Location/City</span>
                                <span className="detail-value-v2">{report.personalInfo?.city || report.personalInfo?.cityType || 'N/A'}</span>
                            </div>
                            <div className="detail-item-v2">
                                <span className="detail-label-v2">Generated On</span>
                                <span className="detail-value-v2">{formatDate(report.createdAt)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Financial Core Breakdown */}
                    {calculations?.incomeType === 'partnership' ? (
                        <div className="detail-card-v2">
                            <h2>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>
                                Business Taxation Analysis
                            </h2>
                            <div className="detail-grid-v2">
                                <div className="detail-item-v2">
                                    <span className="detail-label-v2">Total Revenue</span>
                                    <span className="detail-value-v2 amount">{formatCurrency(calculations.salesTurnover)}</span>
                                </div>
                                <div className="detail-item-v2">
                                    <span className="detail-label-v2">Operating Expenses</span>
                                    <span className="detail-value-v2 amount">{formatCurrency(calculations.totalExpenses)}</span>
                                </div>
                                <div className="detail-item-v2">
                                    <span className="detail-label-v2">Taxable Profit</span>
                                    <span className="detail-value-v2 amount" style={{ color: '#10B981' }}>{formatCurrency(calculations.taxableIncome)}</span>
                                </div>
                                <div className="detail-item-v2">
                                    <span className="detail-label-v2">Partner Remuneration</span>
                                    <span className="detail-value-v2 amount">{formatCurrency(calculations.remuneration)}</span>
                                </div>
                                <div className="detail-item-v2">
                                    <span className="detail-label-v2">Capital Interest</span>
                                    <span className="detail-value-v2 amount">{formatCurrency(calculations.interest)}</span>
                                </div>
                                <div className="detail-item-v2">
                                    <span className="detail-label-v2">Total Tax Liability</span>
                                    <span className="detail-value-v2 amount" style={{ color: '#EF4444' }}>{formatCurrency(calculations.totalTaxPayable)}</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="detail-card-v2">
                                <h2>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
                                    Income & Surplus
                                </h2>
                                <div className="detail-grid-v2">
                                    <div className="detail-item-v2">
                                        <span className="detail-label-v2">Monthly Salary</span>
                                        <span className="detail-value-v2 amount">{formatCurrency(calculations?.baseSalary)}</span>
                                    </div>
                                    <div className="detail-item-v2">
                                        <span className="detail-label-v2">Other Sources</span>
                                        <span className="detail-value-v2 amount">{formatCurrency(calculations?.otherIncome)}</span>
                                    </div>
                                    <div className="detail-item-v2">
                                        <span className="detail-label-v2">Total Disposable</span>
                                        <span className="detail-value-v2 amount" style={{ color: '#10B981' }}>{formatCurrency(calculations?.monthlySalary)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="detail-card-v2">
                                <h2>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" /><path d="M3 6h18" /><path d="M16 10a4 4 0 0 1-8 0" /></svg>
                                    Monthly Outgoings
                                </h2>
                                <div className="detail-grid-v2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
                                    {report.expenses && Object.entries(report.expenses).map(([key, value]) => {
                                        if (key === 'totalExpenses' || key === 'surplus') return null;
                                        return (
                                            <div className="detail-item-v2" key={key}>
                                                <span className="detail-label-v2" style={{ fontSize: '10px' }}>{key.replace(/([A-Z])/g, ' $1')}</span>
                                                <span className="detail-value-v2">{formatCurrency(value)}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between' }}>
                                    <div>
                                        <span className="detail-label-v2">Total Expenses:</span>
                                        <span className="detail-value-v2 amount" style={{ marginLeft: '8px' }}>{formatCurrency(calculations?.totalExpenses)}</span>
                                    </div>
                                    <div>
                                        <span className="detail-label-v2">Net Savings:</span>
                                        <span className="detail-value-v2 amount" style={{ marginLeft: '8px', color: calculations?.monthlySavings >= 0 ? '#10B981' : '#EF4444' }}>
                                            {formatCurrency(calculations?.monthlySavings)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div className="sidebar-details-v2">
                    {/* Health Score Card */}
                    <div className="detail-card-v2" style={{ textAlign: 'center' }}>
                        <span className="detail-label-v2">Stability Score</span>
                        <div style={{ fontSize: '48px', fontWeight: 800, margin: '16px 0', color: 'var(--admin-v2-primary)' }}>
                            {calculations?.healthScore}
                        </div>
                        <span className={`health-badge-v2 ${getHealthScoreClass(calculations?.healthScore)}-v2`}>
                            {calculations?.healthScore >= 80 ? 'Excellent' : calculations?.healthScore >= 60 ? 'Stable' : 'Risk Alert'}
                        </span>
                        <div style={{ marginTop: '20px', fontSize: '12px', color: 'var(--admin-v2-text-muted)', lineHeight: 1.5 }}>
                            Based on savings rate of <strong>{calculations?.savingsPercentage}%</strong>
                        </div>
                    </div>

                    {/* Tax Recommendation */}
                    <div className="detail-card-v2">
                        <h2 style={{ fontSize: '15px' }}>Tax Optimization</h2>
                        <div style={{ marginBottom: '16px' }}>
                            <span className="detail-label-v2">Recommended</span>
                            <div style={{ fontSize: '16px', fontWeight: 700, color: '#10B981' }}>
                                {calculations?.incomeType === 'partnership' ? 'Corporate Flat 30%' : calculations?.bestRegime}
                            </div>
                        </div>
                        {calculations?.incomeType !== 'partnership' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: '12px', color: 'var(--admin-v2-text-muted)' }}>Old Regime:</span>
                                    <span style={{ fontSize: '12px', fontWeight: 600 }}>{formatCurrency(calculations?.oldRegimeTax / 12)}/mo</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: '12px', color: 'var(--admin-v2-text-muted)' }}>New Regime:</span>
                                    <span style={{ fontSize: '12px', fontWeight: 600 }}>{formatCurrency(calculations?.newRegimeTax / 12)}/mo</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Transaction Status */}
                    <div className="detail-card-v2">
                        <h2 style={{ fontSize: '15px' }}>Payment Audit</h2>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div>
                                <span className="detail-label-v2">Status</span>
                                {report.status === 'incomplete' ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#ef4444', fontWeight: 700, fontSize: '13px' }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                        Incomplete Form / Unpaid
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#10B981', fontWeight: 700, fontSize: '13px' }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                        Verified Paid
                                    </div>
                                )}
                            </div>
                            <div>
                                <span className="detail-label-v2">Transaction ID</span>
                                <div style={{ fontSize: '11px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                    {report.status === 'incomplete' ? 'N/A' : (report.payment?.paymentId || 'N/A')}
                                </div>
                            </div>
                            <div>
                                <span className="detail-label-v2">Amount</span>
                                <div style={{ fontSize: '14px', fontWeight: 600 }}>
                                    {report.status === 'incomplete' ? (
                                        <span style={{ color: '#ef4444' }}>incomplete form</span>
                                    ) : (
                                        <div className="report-amount-badge">
                                            {formatCurrency(report.payment?.amount || 299)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DiagnosisReportDetail;
